// Centralized age range catalog and pure mapping utilities
// Units: years. Endpoints inclusive. Non-overlapping ranges by design.

export type AgeBound = number | null;

export interface AgeRange {
  min: AgeBound;
  max: AgeBound;
}

export interface AgeCatalogItem extends AgeRange {
  label: string;
}

// Single source of truth for allowed age ranges in UI dropdowns
// Updated to match the exact dropdown values shown in the UI
export const AgeRangeCatalog: AgeCatalogItem[] = [
  { label: 'All ages treated', min: null, max: null }, // No age restriction
  { label: '6 months & up', min: 1, max: null }, // 6 months = 0.5 years
  { label: '2 years and older', min: 2, max: null },
  { label: '5 years and older', min: 5, max: null },
  { label: '9 and up', min: 9, max: null },
  { label: '10 and up', min: 10, max: null },
  { label: '12 and up', min: 12, max: null },
  { label: '13 years and older', min: 13, max: null },
  { label: '14 and up', min: 14, max: null },
  { label: '15 and up', min: 15, max: null },
  { label: '16 and up', min: 16, max: null },
  { label: '17 and up', min: 17, max: null },
  { label: '18 and older', min: 18, max: null },
  { label: '19 years old and up', min: 19, max: null },
  { label: '21 and up', min: 21, max: null },
  { label:'22 years and older', min: 22, max: null},
  { label: '35 and up', min: 35, max: null },
  {label: '40 years and older', min: 40, max: null},
  { label: '55 and older', min: 55, max: null },
  { label: 'Birth-17 years', min: 0, max: 17 },
];

// Deterministic match against catalog
export function labelToRange(label: string): AgeRange {
  const item = AgeRangeCatalog.find((i) => i.label === label);
  if (!item) {
    throw new Error('Unknown age label');
  }
  return { min: item.min, max: item.max };
}

// Normalize potential inputs to numbers/null; supports decimals for fractional ages
export function normalizeAgeBound(value: unknown): AgeBound {
  if (value === null || value === undefined || value === '')
    return null;
  if (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0
  )
    return value;
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return null;
}

export function isValidRange(range: AgeRange): boolean {
  const { min, max } = range;
  if (min === null && max === null) return true;
  if (min === null && max !== null) return max >= 0 && max <= 130;
  if (min !== null && max === null) return min >= 0 && min <= 130;
  if (min !== null && max !== null) {
    if (min < 0 || max < 0 || min > 130 || max > 130) return false;
    return min <= max;
  }
  return false;
}

// Format arbitrary range to human label per rules; used for non-catalog ranges too
export function formatRangeLabel(range: AgeRange): string {
  const { min, max } = range;
  if (min === null && max === null) return 'All ages treated';
  if (min !== null && max === null) return `${min}+`;
  if (min === null && max !== null) return `≤${max}`;
  if (min !== null && max !== null) {
    // Treat sentinel upper bound (125) as open-ended for UI labels
    if (max >= 125) return `${min}+`;
    if (min === max) return `Age ${min}`;
    return `${min}–${max}`;
  }
  return 'All ages treated';
}

// Map backend range to catalog label, handling coerced values
export function rangeToLabel(range: AgeRange): string {
  const { min, max } = range;

  // Handle backend-coerced values: if both min=1 and max=125 (defaults), treat as "All ages treated"
  // This fixes the bug where no age restriction was incorrectly showing as "6 months & up"
  if (min === 1 && max === 125) {
    return 'All ages treated';
  }

  // For null/null, show "All ages treated" for display purposes
  // This represents no age restriction (not the unselected state)
  if (min === null && max === null) {
    return 'All ages treated';
  }

  // First try exact match for non-null ranges
  const exactMatch = AgeRangeCatalog.find(
    (i) => i.min === min && i.max === max
  );

  if (exactMatch) {
    return exactMatch.label;
  }

  // Handle backend-coerced values: if max is 125 (default), treat as open-ended
  if (max === 125 && min !== null) {
    const openEndedMatch = AgeRangeCatalog.find(
      (i) => i.min === min && i.max === null
    );
    if (openEndedMatch) {
      return openEndedMatch.label;
    }
  }

  // Handle backend-coerced values: if min is 1 (default), treat as max-only
  if (min === 1 && max !== null) {
    const maxOnlyMatch = AgeRangeCatalog.find(
      (i) => i.min === null && i.max === max
    );
    if (maxOnlyMatch) {
      return maxOnlyMatch.label;
    }
  }

  // For truly custom ranges that don't match any catalog entry
  return `Custom: ${formatRangeLabel(range)}`;
}

// Helper to map legacy/plans string age to range (e.g., "All ages treated" or "10-125")
export function parseBackendAgeString(
  age: string | null | undefined
): AgeRange {
  if (!age) return { min: null, max: null };
  const normalized = age.trim();
  if (/^all\s*ages?$/i.test(normalized))
    return { min: null, max: null };
  // "X-Y" or "X–Y" range
  let m = normalized.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (m) {
    const min = normalizeAgeBound(m[1]);
    const max = normalizeAgeBound(m[2]);
    return { min, max };
  }
  // "X+"
  m = normalized.match(/^(\d+)\+$/);
  if (m) {
    const min = normalizeAgeBound(m[1]);
    return { min, max: null };
  }
  // "≤Y" or "<=Y"
  m = normalized.match(/^≤\s*(\d+)$/);
  if (m) {
    const max = normalizeAgeBound(m[1]);
    return { min: null, max };
  }
  m = normalized.match(/^<=\s*(\d+)$/);
  if (m) {
    const max = normalizeAgeBound(m[1]);
    return { min: null, max };
  }
  // "Age X"
  m = normalized.match(/^Age\s+(\d+)$/i);
  if (m) {
    const n = normalizeAgeBound(m[1]);
    const value = typeof n === 'number' ? n : null;
    return { min: value, max: value };
  }
  // Unknown -> null,null
  return { min: null, max: null };
}

// Convert range to backend expectations: explicit integers or nulls
export function coerceRangeForBackend(
  range: AgeRange,
  options?: { minDefault?: number; maxDefault?: number }
): { minimumAge: number | null; maximumAge: number | null } {
  const { min, max } = range;

  // For null/null (unselected), send nulls to backend
  if (min === null && max === null) {
    return { minimumAge: null, maximumAge: null };
  }

  const minDefault = options?.minDefault ?? 1;
  const maxDefault = options?.maxDefault ?? 125;

  // Map open-ended to numeric bounds expected by backend
  const minimumAge = (min === null ? minDefault : min) as number;
  const maximumAge = (max === null ? maxDefault : max) as number;
  return { minimumAge, maximumAge };
}

// Generate stable row key for consistent identity across pagination/search
export function generateRowKey(
  planId: string | undefined,
  providerId: string,
  practiceId: string
): string {
  return `${planId || 'unknown'}-${providerId}-${practiceId}`;
}

// Reconstruct age range from backend data with preference for numeric fields
export function reconstructAgeRange(plan: {
  minimumAge?: number | null;
  maximumAge?: number | null;
  age?: string | null;
  metadata?: {
    minimumAge?: number | null;
    maximumAge?: number | null;
  };
}): AgeRange {
  // First check if we have numeric fields directly on the plan
  if (
    plan.minimumAge !== undefined ||
    plan.maximumAge !== undefined
  ) {
    // Handle backend-coerced defaults: if both are default values, treat as "All ages treated"
    if (plan.minimumAge === 1 && plan.maximumAge === 125) {
      return { min: null, max: null };
    }
    return {
      min: plan.minimumAge ?? null,
      max: plan.maximumAge ?? null,
    };
  }

  // Check metadata object (new backend structure)
  if (
    plan.metadata &&
    (plan.metadata.minimumAge !== undefined ||
      plan.metadata.maximumAge !== undefined)
  ) {
    // Handle backend-coerced defaults: if both are default values, treat as "All ages treated"
    if (
      plan.metadata.minimumAge === 1 &&
      plan.metadata.maximumAge === 125
    ) {
      return { min: null, max: null };
    }
    return {
      min: plan.metadata.minimumAge ?? null,
      max: plan.metadata.maximumAge ?? null,
    };
  }

  // Fallback to parsing age string
  return parseBackendAgeString(plan.age);
}


